import React from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import Field from 'legacy/common/components/wysiwyg-form/Field';
import ToggleField from 'legacy/common/components/wysiwyg-form/ToggleField';
import TicketQuestions from '../../../../TicketQuestions';
import { Popup } from 'semantic-ui-react';
import { CURRENCY_CODES } from 'legacy/util/constants';
import { UserMe } from 'legacy/user/types';

import styles from '../../../FormStyles.module.scss';

interface StandardProductFieldsProps {
  isAddon?: boolean;
  user: UserMe;
  isUnsaved: (field: string) => boolean;
}

const StandardProductFields: React.FC<StandardProductFieldsProps> = ({
  isAddon,
  user,
  isUnsaved
}) => {
  const { t } = useTranslation(['events']);
  const { values } = useFormikContext<any>();
  const productTypeLabel = isAddon
    ? t('events:pricingTiers.ticketForm.availableItems')
    : t('events:pricingTiers.ticketForm.availableTickets');

  return (
    <>
      {/* Common fields specifically configured for Standard products */}
      <div>
        <Field
          name="name"
          id="name"
          type="text"
          label={`${
            isAddon
              ? t('events:pricingTiers.ticketForm.addonName')
              : t('events:pricingTiers.ticketForm.ticketName')
          }* ${
            isUnsaved('name') ? t('events:pricingTiers.ticketForm.unsaved') : ''
          }`}
          disabled={!values.canDelete}
          placeholder={
            isAddon
              ? t('events:pricingTiers.ticketForm.addonNamePlaceholder')
              : t('events:pricingTiers.ticketForm.ticketNamePlaceholder')
          }
        />
        <Field
          type="text"
          id="description"
          name="description"
          label={`${t('events:pricingTiers.ticketForm.description')}* ${
            isUnsaved('description')
              ? t('events:pricingTiers.ticketForm.unsaved')
              : ''
          }`}
          placeholder={
            isAddon
              ? t('events:pricingTiers.ticketForm.addonDescriptionPlaceholder')
              : t('events:pricingTiers.ticketForm.descriptionPlaceholder')
          }
        />
      </div>

      <div className="flex gap-6">
        <Field
          id="maxQuantity"
          name="maxQuantity"
          type="number"
          label={`${productTypeLabel}*`}
          placeholder={t(
            'events:pricingTiers.ticketForm.availableQuantityPlaceholder'
          )}
        />

        {/* Price field */}
        {!values.canDelete && (
          <span className={styles.tooltip}>
            <Popup
              hoverable
              position="right center"
              trigger={<i className="fa fa-info-circle" />}
              size="tiny"
              content={t('events:pricingTiers.ticketForm.priceCannotBeEdited')}
            />
          </span>
        )}

        <Field
          id="price"
          name="price"
          type="number"
          disabled={!values.canDelete}
          label={`${t('events:pricingTiers.ticketForm.price')} (${
            CURRENCY_CODES[user?.country]
          })* ${
            isUnsaved('price')
              ? t('events:pricingTiers.ticketForm.unsaved')
              : ''
          }`}
          placeholder={t('events:pricingTiers.ticketForm.pricePlaceholder')}
        />
      </div>

      <div className="mt-6">
        <ToggleField
          name="purchaseRequirements.requiresApproval"
          label={t('events:pricingTiers.ticketForm.requiresApproval')}
        />
        {values.purchaseRequirements?.requiresApproval && (
          <TicketQuestions name="purchaseRequirements.questions" />
        )}
      </div>
    </>
  );
};

export default StandardProductFields;
